import { motion } from 'framer-motion'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'

interface Reviewer {
    name: string
    affiliation: string
    email?: string
}

const REVIEWERS: Reviewer[] = [
    { name: 'Prof. Bijayalaxmi Nanda', affiliation: 'Principal & Professor, Miranda House, University of Delhi', email: 'bijayalaxmi.nanda@mirandahouse.ac.in' },
    { name: 'Prof. Sanjay Kumar', affiliation: 'Professor, Centre for the Study of Developing Societies (CSDS), New Delhi', email: 'sanjay@csds.in' },
    { name: 'Prof. Atanu Mahapatra', affiliation: 'Centre for Diaspora Studies, Central University of Gujarat', email: 'atanu.kumar@cug.ac.in' },
    { name: 'Prof. Abhay Prasad Singh', affiliation: 'Professor, Political Science, P.G.D.A.V. College, University of Delhi', email: 'abhay.singh@pgdav.du.ac.in' },
    { name: 'Prof. Priya Ranjan Singh', affiliation: 'Retired Professor, Political Science, A N College, Patna (Patliputra University)' },
    { name: 'Dr. Rashmi Gopi', affiliation: 'Associate Professor, Department of Political Science, Miranda House, University of Delhi', email: 'rashmi.gopi@mirandahouse.ac.in' },
    { name: 'Dr. Mithila Bagai', affiliation: 'Senior Assistant Professor, Department of Political Science, University of Delhi', email: 'mithilabagai@gmail.com' },
    { name: 'Dr. Abhishek Choudhary', affiliation: 'Senior Assistant Professor, Department of Political Science, University of Delhi', email: 'achoudhary@polscience.du.ac.in' },
    { name: 'Dr. Koiremba Ningthoujam Singh', affiliation: 'Senior Assistant Professor, Department of Political Science, University of Delhi', email: 'koirembasingh@yahoo.com' },
    { name: 'Dr. Sonali Chitalkar', affiliation: 'Assistant Professor, Department of Political Science, Miranda House, University of Delhi', email: 'sonali.chitalkar@mirandahouse.ac.in' },
    { name: 'Dr. Manoj Kumar Rai', affiliation: 'Assistant Professor, Department of Gandhi & Peace Studies, Mahatma Gandhi Antarrashtriya Hindi Vishwavidyalaya, Wardha', email: 'chinmay69@gmail.com' },
    { name: 'Dr. Krishna Murari', affiliation: 'Faculty, Aryabhatta College, University of Delhi', email: 'murari@aryabhattacollege.ac.in' },
    { name: 'Dr. Avantika Singh', affiliation: 'Assistant Professor, Department of Political Science, University of Delhi', email: 'asingh@polscience.du.ac.in' },
    { name: 'Dr. Pranav Kumar', affiliation: 'Assistant Professor, Centre for the Study of Social Exclusion & Inclusive Policy, JNU', email: 'pranavkumar@mail.jnu.ac.in' },
    { name: 'Dr. Uttam Sinha', affiliation: 'Senior Fellow, Manohar Parrikar Institute for Defence Studies and Analyses', email: 'uksinha.idsa@nic.in' },
    { name: 'Dr. Nitesh Rai', affiliation: 'Assistant Professor, Department of Political Science, Daulat Ram College, University of Delhi', email: 'nitesh@dr.du.ac.in' },
    { name: 'Dr. Sanjay Kumar Aggarwal', affiliation: 'Assistant Professor, Department of Political Science & Public Administration, Central University of Jharkhand', email: 'sanjay.agrawal@cuj.ac.in' },
    { name: 'Dr. Miranda Das', affiliation: 'Assistant Professor, Department of Political Science, University of Delhi', email: 'das.jan.miranda@gmail.com' },
    { name: 'Dr. Ekta Shaikh', affiliation: 'Assistant Professor, Department of Political Science, Dyal Singh College (Evening), University of Delhi', email: 'ektashaikh93@gmail.com' },
    { name: 'Dr. Raushan Thakur', affiliation: 'Assistant Professor (Guest), Department of Political Science, Kirori Mal College, University of Delhi', email: 'raushanworld@gmail.com' },
    { name: 'Ms. Shambhavi Mani', affiliation: 'Assistant Professor, Department of Political Science, Vivekananda College, University of Delhi', email: 'shambhavimani02@gmail.com' },
    { name: 'Mr. Rishabh Raj', affiliation: 'Assistant Professor, Department of Political Science, Shri Ram College of Commerce, University of Delhi', email: 'rajrishabh011@gmail.com' },
    { name: 'Mr. Prateek Kashyap', affiliation: 'Assistant Professor, Department of Political Science, College of Vocational Studies, University of Delhi', email: 'pk991995@gmail.com' }
]

function ReviewerCard({ r, index }: { r: Reviewer; index: number }) {
    return (
        <motion.figure
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: (index % 3) * 0.05 }}
            className="flex flex-col h-full px-6 py-6"
            style={{
                background: 'var(--color-paper-50)',
                border: '1px solid var(--color-rule)',
                borderTop: '2px solid var(--color-accent-500)'
            }}
        >
            <div
                className="font-display"
                style={{ fontSize: '1.15rem', lineHeight: 1.2, color: 'var(--color-ink-700)', letterSpacing: '-0.014em', fontWeight: 500 }}
            >
                {r.name}
            </div>
            <div className="mt-3 text-[13.5px] leading-relaxed flex-1" style={{ color: 'var(--color-ink-400)' }}>
                {r.affiliation}
            </div>
            {r.email && (
                <a
                    href={`mailto:${r.email}`}
                    className="mt-3 inline-block text-[12px] break-all hover:underline"
                    style={{ color: 'var(--color-accent-700)' }}
                >
                    {r.email}
                </a>
            )}
        </motion.figure>
    )
}

export default function ReviewersList() {
    return (
        <>
            <SEO
                title="Reviewer's List"
                description="Acknowledgement and panel of expert peer reviewers supporting the double-blind peer-review process of Indian Studies Review."
                path="/reviewers-list"
            />
            <PageHeader
                eyebrow="For Reviewers"
                title="Acknowledgement of Peer Reviewers"
                lede="The scholars whose rigorous evaluation and constructive feedback uphold the academic standards of Indian Studies Review."
            />

            <section className="section-tight">
                <div className="container-prose" style={{ maxWidth: '52rem' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="prose-body dropcap"
                    >
                        <p>
                            The Centre for Study of Politics and Governance sincerely acknowledges the valuable
                            contribution of the following scholars as peer reviewers for our interdisciplinary
                            journal, <em>Indian Studies Review</em>. Their intellectual engagement, rigorous
                            evaluation, and constructive feedback play a crucial role in maintaining the academic
                            standards and upholding the scholarly credentials of the journal.
                        </p>
                        <p>
                            Some of the listed scholars are actively engaged in the peer-review process for Indian
                            Studies Review, while others have graciously expressed their willingness to contribute as
                            peer reviewers in the future whenever approached. We deeply appreciate their commitment to
                            academic excellence and their support in strengthening the integrity and quality of our
                            publication.
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="section" style={{ borderTop: '1px solid var(--color-rule)' }}>
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-center mb-12"
                    >
                        <div className="kicker kicker--center">For Reviewers</div>
                        <h2 className="section-title mt-5">List of Peer Reviewers</h2>
                    </motion.div>

                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {REVIEWERS.map((r, i) => (
                            <li key={r.name} className="h-full">
                                <ReviewerCard r={r} index={i} />
                            </li>
                        ))}
                    </ul>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-14 mx-auto text-center px-8 py-9"
                        style={{ maxWidth: '46rem', background: 'var(--color-paper-100)', border: '1px solid var(--color-rule)', borderTop: '2px solid var(--color-gold-500)' }}
                    >
                        <div className="overline">Note</div>
                        <p className="mt-4 text-[14.5px] leading-relaxed" style={{ color: 'var(--color-ink-500)' }}>
                            Reviewers who have chosen to remain anonymous at their request are not included in the
                            above list. The editorial board fully respects their decision and acknowledges their
                            valuable contribution to the peer-review process.
                        </p>
                    </motion.div>
                </div>
            </section>
        </>
    )
}
